import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ICONS from "../../../../assets/Icons";
import CommonToast from "../../../../components/_utils/CommonToast";
import { env } from "../../../../env";
import axiosInstance from "../../../globalInspector/axiosInstance";

const baseUrl = env.REACT_APP_BASE_URL;

export const showErrorToast = (text) =>
  CommonToast({
    text,
    icon: ICONS.ERROR_WITH_WHITE_OUTLINED_CIRCLE,
    variant: "var(--error)",
    buttonNeeded: false,
  });

export const createTaxCompliantAccount = createAsyncThunk(
  "taxCompliantAccounts/create",
  async (requestBody, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}/accounts/tds`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      const responseData = error.response?.data;
      return rejectWithValue(responseData || error.message);
    }
  },
);

export const editTaxCompliantAccountDetails = createAsyncThunk(
  "taxCompliantAccounts/editDetails",
  async ({ id, requestBody }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/tds/${id}/edit_other_details`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const editTaxCompliantAccountBank = createAsyncThunk(
  "taxCompliantAccounts/editBank",
  async ({ id, requestBody }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/tds/${id}`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getTaxSections = createAsyncThunk(
  "taxCompliantAccounts/taxSections",
  async (values, { rejectWithValue }) => {
    const { section_name, search_string, active } = values
      ? values
      : { section_name: null, search_string: null, active: null };

    try {
      const response = await axiosInstance.get(`${baseUrl}/tax_sections`, {
        headers: {
          "X-Api-Key": sessionStorage.getItem("q2p_token"),
        },
        params: {
          section_name: section_name,
          search_string: search_string,
          active: active,
        },
      });

      return response.data.tax_sections;
    } catch (error) {
      showErrorToast(error.message);
      return rejectWithValue(error.response?.data);
    }
  },
);

const taxCompliantAccountsSlice = createSlice({
  name: "taxCompliantAccounts",

  initialState: {
    createTaxCompliantData: null,
    createTaxCompliantLoading: false,
    createTaxCompliantError: null,

    taxAccountEditOtherDetailsData: null,
    taxAccountEditOtherDetailsLoading: false,
    taxAccountEditOtherDetailsError: null,

    taxAccountEditBankDetailsData: null,
    taxAccountEditBankDetailsLoading: false,
    taxAccountEditBankDetailsError: null,

    taxSectionData: null,
    taxSectionLoading: false,
    taxSectionError: null,
  },
  reducers: {
    resetCreateTaxCompliant(state) {
      state.createTaxCompliantData = null;
      state.createTaxCompliantError = null;
    },
    resetTaxBankData(state) {
      state.taxAccountLookupByBankData = null;
    },
    resetTaxEditDetails(state) {
      state.taxAccountEditOtherDetailsData = null;
      state.taxAccountEditOtherDetailsError = null;
      state.taxAccountEditOtherDetailsLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createTaxCompliantAccount.pending, (state) => {
        state.createTaxCompliantLoading = true;
        state.createTaxCompliantError = null;
      })
      .addCase(createTaxCompliantAccount.fulfilled, (state, action) => {
        state.createTaxCompliantLoading = false;
        state.createTaxCompliantData = action.payload;
      })
      .addCase(createTaxCompliantAccount.rejected, (state, action) => {
        state.createTaxCompliantLoading = false;
        state.createTaxCompliantError = action.payload;
      })
      .addCase(editTaxCompliantAccountDetails.pending, (state) => {
        state.taxAccountEditOtherDetailsLoading = true;
      })
      .addCase(editTaxCompliantAccountDetails.fulfilled, (state, action) => {
        state.taxAccountEditOtherDetailsLoading = false;
        state.taxAccountEditOtherDetailsData = action.payload;
      })
      .addCase(editTaxCompliantAccountDetails.rejected, (state, action) => {
        state.taxAccountEditOtherDetailsLoading = false;
        state.taxAccountEditOtherDetailsError = action.payload;
      })

      .addCase(editTaxCompliantAccountBank.pending, (state) => {
        state.taxAccountEditBankDetailsLoading = true;
      })
      .addCase(editTaxCompliantAccountBank.fulfilled, (state, action) => {
        state.taxAccountEditBankDetailsLoading = false;
        state.taxAccountEditBankDetailsData = action.payload;
      })
      .addCase(editTaxCompliantAccountBank.rejected, (state, action) => {
        state.taxAccountEditBankDetailsLoading = false;
        state.taxAccountEditBankDetailsError = action.payload;
      })

      .addCase(getTaxSections.pending, (state) => {
        state.taxSectionLoading = true;
        state.taxSectionError = null;
      })
      .addCase(getTaxSections.fulfilled, (state, action) => {
        state.taxSectionLoading = false;
        state.taxSectionData = action.payload;
      })
      .addCase(getTaxSections.rejected, (state, action) => {
        state.taxSectionLoading = false;
        state.taxSectionError = action.payload;
      });
  },
});

export const {
  resetCreateTaxCompliant,
  resetTaxBankData,
  resetTaxEditDetails,
} = taxCompliantAccountsSlice.actions;

export default taxCompliantAccountsSlice.reducer;
